import { ContentsSourceEnum } from '../constants/createSendContents';
import { isMarketplaceBucketItem, TCreateSendBucketItem } from '../types/bucket';
import { ICampaign } from '../types/campaigns';

/**
 * What a send's contents are sourced from, once both kits and loose items are considered.
 *
 * `Mixed` exists because the source lock is a Contents-step affordance, not an invariant the
 * rest of the flow can lean on: a draft persisted before the lock shipped, or a kit added
 * beside a marketplace pick in the PYG flow, both reach later steps carrying two sources.
 * Callers that must treat those as one thing should say which one — hence the explicit
 * `isMarketplaceOnlySend` / `hasMarketplaceContent` split below rather than a single boolean.
 */
export enum CreateSendSourceEnum {
  IeCatalog = 'ie-catalog',
  Marketplace = 'marketplace',
  Mixed = 'mixed',
  Empty = 'empty',
}

export type TCreateSendSource = CreateSendSourceEnum;

interface ICreateSendContentsSlice {
  kits?: ICampaign[] | null;
  items?: TCreateSendBucketItem[] | null;
}

/**
 * Kits are always IE campaigns — the marketplace has no kit concept — so a kit's presence is
 * itself IE content and its `items` never need scanning for a marketplace source.
 */
const hasIeContent = ({ kits, items }: ICreateSendContentsSlice): boolean =>
  Boolean(kits?.length) || (items ?? []).some((item) => !isMarketplaceBucketItem(item));

const hasMarketplaceItems = ({ items }: ICreateSendContentsSlice): boolean =>
  (items ?? []).some(isMarketplaceBucketItem);

/**
 * Resolves what a send's contents are sourced from.
 *
 * Derived from the contents themselves — every marketplace pick carries `source: 'marketplace'`
 * from `mapCatalogItem`, so the items in the store are the authority. A campaign id or a link
 * slug is not: the same portal campaign can be reached through either, and neither says whose
 * catalog the products came from.
 */
export const resolveCreateSendSource = (contents: ICreateSendContentsSlice): TCreateSendSource => {
  const isIe = hasIeContent(contents);
  const isMarketplace = hasMarketplaceItems(contents);

  if (isIe && isMarketplace) {
    return CreateSendSourceEnum.Mixed;
  }

  if (isMarketplace) {
    return CreateSendSourceEnum.Marketplace;
  }

  return isIe ? CreateSendSourceEnum.IeCatalog : CreateSendSourceEnum.Empty;
};

/**
 * True when the marketplace is the *only* source — the test for "this is a marketplace send,
 * not a portal-campaign send". Empty contents are not a marketplace send: a sender who has
 * picked nothing yet, or one sending a budget alone, has chosen no catalog.
 */
export const isMarketplaceOnlySend = (contents: ICreateSendContentsSlice): boolean =>
  resolveCreateSendSource(contents) === CreateSendSourceEnum.Marketplace;

/**
 * True when the marketplace is the only source, or one of two. Use for "does this send touch
 * the marketplace at all" questions — provider stock checks, marketplace-specific copy —
 * where a mixed send still needs the marketplace treatment.
 */
export const hasMarketplaceContent = hasMarketplaceItems;

/**
 * True when no marketplace pick is present. Empty contents count as IE-only by this test, so
 * pair it with a selection check where "nothing picked" must be handled separately.
 */
export const isIeCatalogOnlySend = (contents: ICreateSendContentsSlice): boolean => {
  const source = resolveCreateSendSource(contents);

  return source === CreateSendSourceEnum.IeCatalog || source === CreateSendSourceEnum.Empty;
};

/**
 * Bridges to `ContentsSourceEnum`, the Contents-step vocabulary driving the source toggle and
 * lock. `null` for mixed and empty contents: neither is a state the toggle can sit in, and
 * coercing either would silently lock the sender out of one catalog.
 */
export const toContentsSource = (source: TCreateSendSource): ContentsSourceEnum | null => {
  if (source === CreateSendSourceEnum.Marketplace) {
    return ContentsSourceEnum.Marketplace;
  }

  return source === CreateSendSourceEnum.IeCatalog ? ContentsSourceEnum.IeCatalog : null;
};
